// @ts-check

/**
 * @file Renders the user detail route.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams, useRouter} from "expo-router"
import Link from "../../../src/components/link"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, View} from "react-native-propforge"
import Button from "../../../src/components/button"
import buildRoutePath from "../../../src/shared/build-route-path"
import Card from "../../../src/components/card"
import DetailValue from "../../../src/components/detail-value"
import errorMessage from "../../../src/shared/error-message"
import {requiredRouteParam} from "../../../src/shared/route-params"
import {User} from "../../../src/frontend-models/user"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"
import ScreenTitle from "../../../src/components/screen-title"
import Text from "../../../src/components/text"

/** @import {BoardThemeColors} from "../../../src/shared/theme" */

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import Project from "../../../src/frontend-models/project" */
/** @import Task from "../../../src/frontend-models/task" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} UsersShowScreenProps */

/**
 * @typedef {object} UsersShowScreenState
 * @property {boolean} confirmingDelete Whether delete confirmation is visible.
 * @property {boolean} deleting Whether the user is being deleted.
 * @property {boolean} loading Whether user details are loading.
 * @property {Project[]} projects Loaded user projects.
 * @property {{kind: "error", text: string} | null} status Screen status message.
 * @property {Task[]} tasks Loaded assigned tasks.
 * @property {User | null} user Loaded user record.
 */

/**
 * User show screen.
 * @augments {ShapeComponent<UsersShowScreenProps, UsersShowScreenState>}
 */
class UsersShowScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {string} */
  userID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {UsersShowScreenState} */
  state = {
    confirmingDelete: false,
    deleting: false,
    loading: true,
    projects:
      /** @type {Project[]} */ ([]),
    status: /** @type {{kind: "error", text: string} | null} */ null,
    tasks:
      /** @type {Task[]} */ ([]),
    user: /** @type {User | null} */ null
  }

  /** Initializes route params and user loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()

    const userID = requiredRouteParam(params, "id", "User ID")
    this.params = params
    this.router = router
    this.userID = userID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          const user = await User
            .preload({
              tasks: {project: true},
              userProjectLinks: {project: true}
            })
            .find(userID)
          if (cancelled) return
          const projects = user
            .userProjectLinksLoaded()
            .map((userProjectLink) => userProjectLink.project())
            .filter(Boolean)
          const tasks = user.tasksLoaded()
          this.s.projects = projects.sort((projectA, projectB) =>
            String(projectA.name() || "").localeCompare(
              String(projectB.name() || "")
            )
          )
          this.s.tasks = tasks.sort((taskA, taskB) =>
            String(taskA.name() || "").localeCompare(String(taskB.name() || ""))
          )
          this.s.user = user
          this.s.loading = false
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [userID])
  }

  /**
   * Renders user details.
   * @returns {ReactNode} User detail content.
   */
  render() {
    const {confirmingDelete, deleting, loading, projects, status, tasks, user} =
      this.s
    const screenTitle = user
      ? [user.firstName(), user.lastName()].filter(Boolean).join(" ") ||
        user.email()
      : _("User")
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="userShowScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          width: "100%"
        }}
        testID="userShowScreen"
      >
        <ScreenTitle>{screenTitle}</ScreenTitle>
        <View
          testID="userShowScreen/content"
        >
          {status &&
            <Text
              style={styles[`statusError-${this.tt.themeName}`] ||= {
                color: this.tt.colors.dangerText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="userShowScreen/status"
            >
              {status.text}
            </Text>
          }
          {user &&
            <Card
              style={styles.cardSpacing ||= {padding: 20}}
              testID="userShowScreen/card"
            >
              <View
                style={styles.headerRow ||= {
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: 20
                }}
                testID="userShowScreen/headerRow"
              >
                <Text
                  style={styles[`headerTitle-${this.tt.themeName}`] ||= {
                    fontSize: 24,
                    fontWeight: 700
                  }}
                  testID="userShowScreen/title"
                >
                  {screenTitle}
                </Text>
                <Button
                  href={buildRoutePath(this.params, `/users/${this.userID}/edit`)}
                  iconName="pen-to-square"
                  label={_("Edit")}
                  testID="userShowScreen/editButton"
                />
              </View>
              {detailRow({
                colors: this.tt.colors,
                label: _("Email"),
                testID: "email",
                themeName: this.tt.themeName,
                value: user.email() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("First name"),
                testID: "firstName",
                themeName: this.tt.themeName,
                value: user.firstName() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Last name"),
                testID: "lastName",
                themeName: this.tt.themeName,
                value: user.lastName() || "-"
              })}
              <View
                style={styles.section ||= {
                  marginTop: 24
                }}
                testID="userShowScreen/projectsSection"
              >
                <Text
                  style={styles[`sectionTitle-${this.tt.themeName}`] ||= {
                    fontSize: 18,
                    fontWeight: 700,
                    marginBottom: 12
                  }}
                  testID="userShowScreen/projectsSection/title"
                >
                  {_("Projects")}
                </Text>
                {projects.length === 0 &&
                  <Text
                    style={styles[`emptySectionText-${this.tt.themeName}`] ||= {
                      color: this.tt.colors.textMuted
                    }}
                    testID="userShowScreen/projectsSection/empty"
                  >
                    {_("No projects found")}
                  </Text>
                }
                {projects.map((project) => {
                  const projectName = project.name() || _("Unnamed project")

                  return (
                    <Link
                      asChild
                      href={buildRoutePath(this.params, `/projects/${project.id()}`)}
                      key={project.id()}
                    >
                      <Pressable
                        accessibilityLabel={projectName}
                        accessible
                        collapsable={false}
                        style={styles[`projectCard-${this.tt.themeName}`] ||= {
                          backgroundColor: this.tt.colors.surfaceMuted,
                          borderColor: this.tt.colors.border,
                          borderRadius: 10,
                          borderWidth: 1,
                          marginBottom: 10,
                          padding: 12
                        }}
                        testID={`userShowScreen/project-${project.id()}`}
                      >
                        <View
                          accessible={false}
                          collapsable={false}
                          testID={`userShowScreen/project-${project.id()}/title`}
                        >
                          <Text
                            accessibilityLabel={projectName}
                            style={styles[`projectCardTitle-${this.tt.themeName}`] ||= {
                              fontSize: 15,
                              fontWeight: 600
                            }}
                            testID={`userShowScreen/project-${project.id()}/title/text`}
                          >
                            {projectName}
                          </Text>
                        </View>
                      </Pressable>
                    </Link>
                  )
                })}
              </View>
              <View
                style={styles.section}
                testID="userShowScreen/tasksSection"
              >
                <Text
                  style={styles[`sectionTitle-${this.tt.themeName}`]}
                  testID="userShowScreen/tasksSection/title"
                >
                  {_("Tasks")}
                </Text>
                {tasks.length === 0 &&
                  <Text
                    style={styles[`emptySectionText-${this.tt.themeName}`]}
                    testID="userShowScreen/tasksSection/empty"
                  >
                    {_("No tasks found")}
                  </Text>
                }
                {tasks.map((task) => {
                  const taskName = task.name() || _("Unnamed task")

                  return (
                    <Link
                      asChild
                      href={buildRoutePath(this.params, `/tasks/${task.id()}?projectId=${encodeURIComponent(task.projectId() || "")}`)}
                      key={task.id()}
                    >
                      <Pressable
                        accessibilityLabel={taskName}
                        accessible
                        collapsable={false}
                        style={styles[`taskCard-${this.tt.themeName}`] ||= {
                          backgroundColor: this.tt.colors.surfaceMuted,
                          borderColor: this.tt.colors.border,
                          borderRadius: 10,
                          borderWidth: 1,
                          marginBottom: 10,
                          padding: 12
                        }}
                        testID={`userShowScreen/task-${task.id()}`}
                      >
                        <View
                          accessible={false}
                          collapsable={false}
                          testID={`userShowScreen/task-${task.id()}/title`}
                        >
                          <Text
                            accessibilityLabel={taskName}
                            style={styles[`taskCardTitle-${this.tt.themeName}`] ||= {
                              fontSize: 15,
                              fontWeight: 600
                            }}
                            testID={`userShowScreen/task-${task.id()}/title/text`}
                          >
                            {taskName}
                          </Text>
                        </View>
                        <Text
                          style={styles[`taskCardSubtitle-${this.tt.themeName}`] ||= {
                            color: this.tt.colors.textMuted,
                            fontSize: 13,
                            marginTop: 4
                          }}
                          testID={`userShowScreen/task-${task.id()}/project`}
                        >
                          {task.project()?.name() || "-"}
                        </Text>
                      </Pressable>
                    </Link>
                  )
                })}
              </View>
              <View
                style={styles.section}
                testID="userShowScreen/destroySection"
              >
                {!confirmingDelete &&
                  <Button
                    disabled={deleting}
                    iconName="trash"
                    label={_("Delete user")}
                    onPress={this.tt.onDeletePress}
                    testID="userShowScreen/deleteButton"
                    variant="destructive"
                  />
                }
                {confirmingDelete &&
                  <View
                    style={styles.deleteConfirmRow ||= {
                      flexDirection: "row",
                      gap: 10
                    }}
                    testID="userShowScreen/deleteConfirm"
                  >
                    <Button
                      disabled={deleting}
                      iconName="trash"
                      label={deleting ? _("Deleting...") : _("Confirm delete")}
                      onPress={this.tt.onConfirmDeletePress}
                      testID="userShowScreen/confirmDeleteButton"
                      variant="destructive"
                    />
                    <Button
                      disabled={deleting}
                      iconName="xmark"
                      label={_("Cancel")}
                      onPress={this.tt.onCancelDeletePress}
                      testID="userShowScreen/cancelDeleteButton"
                      variant="outline"
                    />
                  </View>
                }
              </View>
            </Card>
          }
        </View>
      </View>
    )
  }

  /** Starts delete confirmation. */
  onDeletePress = () => {
    this.s.confirmingDelete = true
  }

  /** Cancels delete confirmation. */
  onCancelDeletePress = () => {
    this.s.confirmingDelete = false
  }

  /**
   * Deletes the loaded user.
   * @returns {Promise<void>} Resolves when delete handling completes.
   */
  onConfirmDeletePress = async () => {
    const {params, router} = this.tt
    const {user} = this.s

    if (!user) return

    this.s.deleting = true
    this.s.status = null

    try {
      await user.destroy()
      router.replace(buildRoutePath(params, "/users"))
    } catch (error) {
      this.s.confirmingDelete = false
      this.s.deleting = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }

}

/** @type {NamedExoticComponent<UsersShowScreenProps>} */
const UsersShowScreenComponent = memo(shapeComponent(UsersShowScreen))

export default UsersShowScreenComponent

/**
 * Renders one user detail row.
 * @param {{colors: BoardThemeColors, label: string, testID: string, themeName: string, value: string}} args Detail row values.
 * @returns {ReactNode} Rendered metadata row.
 */
function detailRow({colors, label, testID, themeName, value}) {
  return (
    <View
      collapsable={false}
      style={styles[`detailRow-${themeName}`] ||= {
        borderBottomColor: colors.divider,
        borderBottomWidth: 1,
        paddingVertical: 10
      }}
      testID={`userShowScreen/${testID}/row`}
    >
      <Text
        style={styles[`detailLabel-${themeName}`] ||= {
          color: colors.textMuted,
          fontWeight: 600,
          marginBottom: 4
        }}
        testID={`userShowScreen/${testID}/label`}
      >
        {label}
      </Text>
      <DetailValue
        style={styles[`detailValue-${themeName}`] ||= {
          fontSize: 16
        }}
        testID={`userShowScreen/${testID}/value`}
        value={value}
      />
    </View>
  )
}
